import React, { useRef, useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { PlusCircle, Trash2, Loader2 } from 'lucide-react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';

import API from '@/apiRequest';
import Modal from '@/components/common/Modal';
import { Button } from '@/components/ui/button';
import { showToast } from '@/store/ducks/toasts';
import AttachmentCircle from '@/svg-icons/attachments';

/**
 * AttachmentFiles — custom RJSF field component for multi-file PDF attachments.
 * Uses local state for files to ensure immediate UI feedback on upload/delete,
 * synced from formData prop when external changes occur (e.g. form reset).
 */
const AttachmentFiles = ({ formData, onChange, name, schema, disabled, rawErrors }) => {
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
  const [originalNameToDelete, setOriginalNameToDelete] = useState('');

  const [files, setFiles] = useState(() => {
    if (Array.isArray(formData) && formData.length) return formData;
    return [];
  });

  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const intl = useIntl();

  useEffect(() => {
    const incoming = Array.isArray(formData) ? formData : [];
    const isDifferent = files.length !== incoming.length ||
      files.some((f, i) => f?.original_name !== incoming[i]?.original_name);
    if (isDifferent) {
      setFiles(incoming);
    }
  }, [formData]);

  const handleUploadAttachment = async event => {
    setIsUploadingFile(true);

    try {
      const file = event.target.files[0];
      if (fileInputRef.current) fileInputRef.current.value = null;

      if (!file) {
        setIsUploadingFile(false);
        return;
      }

      if (file.type !== 'application/pdf') {
        dispatch(
          showToast({
            type: 'error',
            title: intl.formatMessage({ id: 'ERROR' }),
            text: intl.formatMessage({ id: 'ATTACH_ONLY_PDF_FILES' }),
            duration: 5000,
          })
        );
        setIsUploadingFile(false);
        return;
      }

      const uploadResponse = await API.get('/upload');
      const uploadUrlResponse = await fetch(uploadResponse?.data?.url, {
        method: 'PUT',
        headers: { 'Content-Type': 'binary/octet-stream' },
        body: file,
      });

      if (!uploadUrlResponse?.ok) throw new Error('Error to upload file');

      const originalName = file?.name.split('.pdf')[0];
      const fileNameUniqueId = uuidv4();

      const newFile = {
        original_name: `${originalName}-${fileNameUniqueId}.pdf`,
        remote_name: uploadResponse?.data?.remote_name,
        extension: '.pdf',
      };

      setFiles(prev => {
        const updated = [...prev, newFile];
        onChange(updated);
        return updated;
      });

      dispatch(
        showToast({
          type: 'success',
          title: intl.formatMessage({ id: 'SUCCESS' }),
          text: intl.formatMessage({ id: 'FILE_UPLOADED_SUCCESSFULLY' }),
          duration: 5000,
        })
      );
      setIsUploadingFile(false);
    } catch (err) {
      console.error('[AttachmentFiles] Upload error:', err);
      dispatch(
        showToast({
          type: 'error',
          title: intl.formatMessage({ id: 'ERROR' }),
          text: intl.formatMessage({ id: 'ERROR_UPLOADING_FILE' }),
          duration: 5000,
        })
      );
      setIsUploadingFile(false);
    }
  };

  const handleDeleteAttachment = () => {
    if (!originalNameToDelete) return;

    setFiles(prev => {
      const updated = prev.filter(file => file?.original_name !== originalNameToDelete);
      onChange(updated);
      return updated;
    });
    setOriginalNameToDelete('');
  };

  const handleDeleteConfirmationModalOpen = originalName => {
    setIsDeleteConfirmationModalOpen(true);
    setOriginalNameToDelete(originalName);
  };

  const handleDeleteConfirmationModalClose = () => {
    setIsDeleteConfirmationModalOpen(false);
    setOriginalNameToDelete('');
  };

  const handleDeleteConfirmationModalConfirmation = () => {
    handleDeleteAttachment();
    handleDeleteConfirmationModalClose();
  };

  const titleKey = name || schema?.title || 'attachments';

  return (
    <>
      {isDeleteConfirmationModalOpen && (
        <Modal onClose={handleDeleteConfirmationModalClose}>
          <div className="p-6 max-w-[400px] w-full bg-white rounded-[2px] shadow-xl">
            <header className="flex items-center gap-3 mb-3">
              <div className="text-[#393939] w-6 h-6 shrink-0">
                <AttachmentCircle viewBox="0 0 34.016 34.016" className="w-full h-full fill-current object-contain" />
              </div>
              <p className="font-bold text-[#212529] text-[14px] m-0">
                <FormattedMessage id="WANT_TO_DELETE_ATTACHED_FILE" />
              </p>
            </header>

            <hr className="border-gray-100 mb-5" />

            <footer className="flex gap-2 justify-end">
              <Button
                variant="outline"
                className="min-w-[132px] h-9 rounded-[2px] bg-[#be1104] hover:bg-[#a00e03] text-white border-none text-[14px] normal-case"
                onClick={handleDeleteConfirmationModalClose}
              >
                <FormattedMessage id="BUTTON.CANCEL" />
              </Button>

              <Button
                variant="default"
                className="min-w-[132px] h-9 rounded-[2px] bg-[#859905] hover:bg-[#728204] text-white border-none text-[14px] normal-case"
                onClick={handleDeleteConfirmationModalConfirmation}
              >
                <FormattedMessage id="BUTTON.CONFIRM" />
              </Button>
            </footer>
          </div>
        </Modal>
      )}

      {/* Modern Section Header - Aligned to 0 grid line */}
      <div className="mb-[0.6vw] mt-[0.6vw] flex items-center gap-2">
        <div className="flex items-center justify-center shrink-0 w-6 h-6 text-[#393939]">
          <AttachmentCircle viewBox="0 0 34.016 34.016" className="w-full h-full fill-current object-contain" />
        </div>
        <span 
          className="font-bold text-[#212529] font-['Open_Sans'] leading-tight"
          style={{ fontSize: '16px', fontWeight: 700 }}
        >
          <FormattedMessage id={titleKey} />
        </span>
      </div>

      {/* Content wrapper with standardized 1vw padding */}
      <div className="row" style={{ paddingLeft: '1vw', paddingRight: '1vw' }}>
        <div className="col-md-12">
          <p 
            className="text-gray-600 font-normal m-0 mb-3 font-['Open_Sans']"
            style={{ fontSize: 'calc(7px + 0.3vw)' }}
          >
            <FormattedMessage id="ATTACH_ADDITIONAL_FILES_FROM_INSPECTION" />
          </p>

          {!!files?.length && (
            <ul className="list-none mb-3 pl-5 space-y-2">
              {files.map(file => (
                <li key={file?.original_name} className="flex items-center gap-2 text-[0.625rem] text-[#007bff] font-['Open_Sans']">
                  <span className="truncate flex-1 hover:underline cursor-default">{file?.original_name}</span>
                  <Trash2 
                    size={16} 
                    className="text-[#111827] cursor-pointer shrink-0 hover:text-red-500 transition-colors" 
                    onClick={() => handleDeleteConfirmationModalOpen(file?.original_name)}
                  />
                </li>
              ))}
            </ul>
          )}

          {isUploadingFile && (
            <div className="mb-3 w-full flex justify-start items-center pl-5">
              <Loader2 size={24} className="animate-spin text-[#5882C1]" />
            </div>
          )}

          <div className="mt-2 text-start">
            <input
              id="attachment-file-upload"
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              className="hidden"
              onChange={handleUploadAttachment}
              disabled={disabled}
            />

            <label 
              htmlFor="attachment-file-upload" 
              className={`cursor-pointer ${disabled ? 'pointer-events-none opacity-50' : ''}`}
            >
              <span className="flex items-center gap-1 text-[#007bff] hover:opacity-80 text-[0.625rem] transition-all active:scale-95 py-1 -ml-1">
                <PlusCircle size={16} className="shrink-0" />
                <FormattedMessage id="ADD_ATTACHMENT" />
              </span>
            </label>
          </div>
          {rawErrors && rawErrors.length > 0 && (
            <p className="order-form-field-error">
              {rawErrors[0]}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

AttachmentFiles.propTypes = {
  formData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  schema: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  rawErrors: PropTypes.array,
};

AttachmentFiles.defaultProps = {
  formData: null,
  name: null,
  disabled: false,
};

export default AttachmentFiles;
